<template>
  <validation-observer tag="div" ref="form">
    <div class="row">
      
      <validation-provider class="col-md-6" vid="product_id" :name="lodash.upperFirst($t('purchase_plans.product'))" rules="required" v-slot="{ valid, errors }">
        <b-form-group
          class="row mb-3 asterisk-after-text"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.product'))"
          :state="errors[0] ? false : (valid ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <product-select-ajax
            :key="`${_uid}-product`"
            v-model.number="model.product_id"
            name="product_id"
            :placeholder="$t('Choose {something}', { something: $t('products.singular') })"
            :state="errors[0] ? false : (valid ? true : null)"
            :params="{is_active__eq: 1}"
            @update:selected="onProductSelected"
          />
        </b-form-group>
      </validation-provider>
      <validation-provider
        class="col-md-6"
        vid="sales_plan_id"
        :name="lodash.upperFirst($t('purchase_plans.sales_plan'))"
        :rules="salesPlanRules"
        v-slot="{ valid, errors }"
      >
        <b-form-group
          :class="['row mb-3', { 'asterisk-after-text': isSalesPlanRequired }]"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.sales_plan_id'))"
          :state="errors[0] ? false : (valid ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <vue-multiselect-ajax
            :key="`${_uid}-sales-plan`"
            name="sales_plan_id"
            v-model.number="model.sales_plan_id"
            url="/v1/sales-plans"
            :custom-label="salesPlanLabel"
            :state="errors[0] ? false : (valid ? true : null)"
            :params="{
              'filter[status]': 'approved',
              'filter[product_id]': model.product_id || undefined,
              include: 'product',
              sort: '-id'
            }"
            @update:selected="onSalesPlanSelected"
          />
        </b-form-group>
      </validation-provider>


      <validation-provider class="col-md-6" vid="submitted_at" :name="lodash.upperFirst($t('purchase_plans.submitted_at'))" rules="required" v-slot="{ valid, errors }">
        <b-form-group
          class="row mb-3 asterisk-after-text"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.submitted_at'))"
          :state="errors[0] ? false : (valid ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <date-picker
            name="submitted_at"
            format="DD MMMM YYYY"
            type="date"
            required
            :state="errors[0] ? false : (valid ? true : null)"
            :value="model.submitted_at"
            @input="model.submitted_at = $event"
          />
        </b-form-group>
      </validation-provider>

      <validation-provider v-if="selectedProduct && selectedProduct.code === 'deposit_goods'" class="col-md-6" vid="payment_type" :name="lodash.upperFirst($t('purchase_plans.payment_type'))" rules="required" v-slot="{ valid, errors }">
        <b-form-group
          class="row mb-3 asterisk-after-text"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.payment_type'))"
          :state="errors[0] ? false : (valid ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <b-form-select
            v-model="model.payment_type"
            :options="paymentTypes"
            :state="errors[0] ? false : (valid ? true : null)"
            name="payment_type"
          />
        </b-form-group>
      </validation-provider>

      <validation-provider class="col-md-6" vid="supervisor_id" :name="lodash.upperFirst($t('purchase_plans.supervisor'))" rules="required" v-slot="{ valid, errors }">
        <b-form-group
          class="row mb-3 asterisk-after-text"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.supervisor'))"
          :state="errors[0] ? false : (valid ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <vue-multiselect-ajax
            :key="`${_uid}-supervisor`"
            name="supervisor_id"
            v-model.number="model.supervisor_id"
            url="/users"
            :params="{ sort: 'name,asc' }"
            :state="errors[0] ? false : (valid ? true : null)"
          />
        </b-form-group>
      </validation-provider>
    </div>

    <hr>
    <div class="row">
      <validation-provider class="col-md-6" vid="partner_id" :name="lodash.upperFirst($t('purchase_plans.partner'))" rules="required" v-slot="{ valid, errors }">
        <b-form-group
          class="row mb-3 asterisk-after-text"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.partner'))"
          :state="errors[0] ? false : (valid ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <customer-select-ajax
            :key="`${_uid}-partner`"
            v-model.number="model.partner_id"
            name="partner_id"
            :params="{ active: 1, type__like_in: 'partner' }"
            :state="errors[0] ? false : (valid ? true : null)"
          />
        </b-form-group>
      </validation-provider>

      <validation-provider class="col-md-6" vid="processing_id" :name="lodash.upperFirst($t('purchase_plans.processing'))" :rules="processingRules" v-slot="{ valid, errors }">
        <b-form-group
          :class="['row mb-3', { 'asterisk-after-text': !isProductX }]"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.processing'))"
          :state="errors[0] ? false : (valid ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <vue-multiselect-ajax
            :key="`${_uid}-processing`"
            ref="processingSelect"
            v-model.number="model.processing_id"
            name="processing_id"
            url="/v1/processing-customers"
            search-key="filter[search]"
            :custom-label="processingCustomerLabel"
            :params="{ sort: 'code' }"
            :state="errors[0] ? false : (valid ? true : null)"
            @update:selected="onProcessingSelected"
          >
            <template v-slot:beforeList>
              <b-button variant="link" v-b-modal.processing-create-modal>
                {{ $t('purchase_plans.add_new_processing') }}
              </b-button>
              <customer-create-modal
                id="processing-create-modal"
                :key="`processing-create-modal-${model.processing_id || 0}`"
                :initial-value="{ type: ['processing'] }"
                :modal-title="$t('purchase_plans.create_new_processing')"
                @created="onProcessingCreated"
              />
            </template>
          </vue-multiselect-ajax>
        </b-form-group>
      </validation-provider>
    </div>

    <div v-if="model.processing_id" class="row">
      <div class="col-md-6">
        <b-form-group
          class="row mb-3"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.limit'))"
        >
          <b-form-input
            id="processing_limit"
            type="text"
            disabled
            name="processing_limit"
            :value="moneyDisplay(processingSummary.limit)"
          />
        </b-form-group>
      </div>

      <div class="col-md-6">
        <b-form-group
          class="row mb-3"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.usage'))"
        >
          <b-form-input
            id="processing_usage"
            type="text"
            disabled
            name="processing_usage"
            :value="moneyDisplay(processingSummary.usage)"
          />
        </b-form-group>
      </div>

      <div class="col-md-6">
        <b-form-group
          class="row mb-3"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.remaining'))"
        >
          <b-form-input
            id="processing_remaining"
            type="text"
            disabled
            name="processing_remaining"
            :value="moneyDisplay(processingSummary.remaining)"
          />
        </b-form-group>
      </div>

      <div class="col-md-6">
        <b-form-group
          class="row mb-3"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.dpd_status'))"
        >
          <div class="d-flex align-items-center" style="min-height: 38px">
            <b-badge :variant="daysPastDueVariant(processingSummary.dpd)" class="d-block"
                     style="padding: 8px 16px">
              {{ formattedDaysPastDue(processingSummary.dpd) }}
            </b-badge>
          </div>
        </b-form-group>
      </div>
    </div>

    <b-alert :show="showProcessingDoesntHaveLimitAlert" variant="danger">
      <h5 class="alert-heading">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M11.0289 4.70733L16.9817 13.3389C17.5536 14.1681 16.96 15.2986 15.9527 15.2986H4.04707C3.03977 15.2986 2.44618 14.1681 3.01806 13.3389L8.97088 4.70733C9.46774 3.98688 10.5321 3.98688 11.0289 4.70733ZM9.39036 11.5234H10.3832L10.5907 7.54793H9.18284L9.39036 11.5234ZM9.34967 12.3982C9.2276 12.5148 9.16656 12.6871 9.16656 12.915C9.16656 13.1347 9.22896 13.3056 9.35374 13.4277C9.47852 13.547 9.65485 13.6067 9.88271 13.6067C10.1052 13.6067 10.2788 13.5457 10.4035 13.4236C10.5283 13.2988 10.5907 13.1293 10.5907 12.915C10.5907 12.6925 10.5283 12.5216 10.4035 12.4023C10.2815 12.2829 10.1079 12.2232 9.88271 12.2232C9.64942 12.2232 9.47174 12.2816 9.34967 12.3982Z" fill="#DC2626"/>
        </svg>
{{ $t('purchase_plans.alert_messages.no_limit_header') }}</h5>
      <p class="processing-alert-message">
        {{ $t('purchase_plans.alert_messages.no_limit_message') }}
      </p>
    </b-alert>

    <b-alert :show="showProcessingSummaryErrorAlert" variant="warning">
      <h5 class="alert-heading">
        {{ $t('purchase_plans.alert_messages.summary_fetch_failed_header') }}
      </h5>
      <p class="processing-alert-message">
        {{ $t('purchase_plans.alert_messages.summary_fetch_failed_message') }}
      </p>
      <b-button
        class="mt-2"
        size="sm"
        variant="outline-warning"
        @click="retryProcessingSummary"
      >
        {{ $t('Retry') }}
      </b-button>
    </b-alert>

    <b-alert :show="showProcessingLimitExceededAlert" variant="danger">
      <h5 class="alert-heading">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M11.0289 4.70733L16.9817 13.3389C17.5536 14.1681 16.96 15.2986 15.9527 15.2986H4.04707C3.03977 15.2986 2.44618 14.1681 3.01806 13.3389L8.97088 4.70733C9.46774 3.98688 10.5321 3.98688 11.0289 4.70733ZM9.39036 11.5234H10.3832L10.5907 7.54793H9.18284L9.39036 11.5234ZM9.34967 12.3982C9.2276 12.5148 9.16656 12.6871 9.16656 12.915C9.16656 13.1347 9.22896 13.3056 9.35374 13.4277C9.47852 13.547 9.65485 13.6067 9.88271 13.6067C10.1052 13.6067 10.2788 13.5457 10.4035 13.4236C10.5283 13.2988 10.5907 13.1293 10.5907 12.915C10.5907 12.6925 10.5283 12.5216 10.4035 12.4023C10.2815 12.2829 10.1079 12.2232 9.88271 12.2232C9.64942 12.2232 9.47174 12.2816 9.34967 12.3982Z" fill="#DC2626"/>
        </svg>
{{ $t('purchase_plans.alert_messages.limit_exceeded_header') }}</h5>
      <p class="processing-alert-message">
        {{ $t('purchase_plans.alert_messages.limit_exceeded_message') }}
      </p>
    </b-alert>

    <hr>
    <div class="row">
      <div class="col-md-6">
        <b-form-group
          class="row mb-3 asterisk-after-text"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.est_size'))"
        >
          <div class="d-flex align-items-center">
            <validation-provider
              class="flex-grow-1"
              :key="`estimated-size-start-${model.estimated_size_end}`"
              vid="estimated_size_start"
              :name="lodash.upperFirst($t('purchase_plans.estimated_size_start'))"
              :rules="estimatedSizeStartRules"
              :custom-messages="{ max_value: $t('purchase_plans.size_start_greater_than_end_warning', { size: model.estimated_size_end }) }"
              v-slot="{ valid, errors }"
            >
              <numeral-strict-input
                v-model.number="model.estimated_size_start"
                name="estimated_size_start"
                class="text-right"
                format="0,0.[000]"
                :state="errors[0] ? false : (valid ? true : null)"
                :placeholder="lodash.upperFirst($t('purchase_plans.size_start_placeholder'))"
              />
              <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
            </validation-provider>

            <span class="mx-2 est-size-separator">-</span>

            <validation-provider
              class="flex-grow-1"
              vid="estimated_size_end"
              :name="lodash.upperFirst($t('purchase_plans.estimated_size_end'))"
              rules="required|double|min_value:0|max_value:100000000000000000000"
              v-slot="{ valid, errors }"
            >
              <numeral-strict-input
                v-model.number="model.estimated_size_end"
                name="estimated_size_end"
                class="text-right"
                format="0,0.[000]"
                :state="errors[0] ? false : (valid ? true : null)"
                :placeholder="lodash.upperFirst($t('purchase_plans.size_end_placeholder'))"
              />
              <small v-if="errors[0]" class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </div>
        </b-form-group>
      </div>

      <validation-provider class="col-md-6" vid="estimated_quantity" :name="lodash.upperFirst($t('purchase_plans.estimated_quantity'))" rules="required|double|min_value:0|max_value:100000000000000000000" v-slot="{ valid, errors }">
        <b-form-group
          class="row mb-3 asterisk-after-text"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.estimated_quantity'))"
          :state="errors[0] ? false : (valid ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <numeral-strict-input
            v-model.number="model.estimated_quantity"
            name="estimated_quantity"
            class="text-right"
            format="0,0.[000]"
            :state="errors[0] ? false : (valid ? true : null)"
            :placeholder="lodash.upperFirst($t('purchase_plans.estimated_quantity'))"
          />
        </b-form-group>
      </validation-provider>
    </div>

    <div class="row">
      <validation-provider class="col-md-6" vid="estimated_amount" :name="lodash.upperFirst($t('purchase_plans.estimated_amount'))" rules="required|double|min_value:0|max_value:100000000000000000000" v-slot="{ valid, errors }">
        <b-form-group
          class="row mb-3 asterisk-after-text"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.estimated_amount'))"
          :state="errors[0] ? false : (valid ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <numeral-strict-input
            v-model.number="model.estimated_amount"
            name="estimated_amount"
            class="text-right"
            format="$ 0,0.[000]"
            :state="errors[0] ? false : (valid ? true : null)"
            :placeholder="lodash.upperFirst($t('purchase_plans.estimated_amount'))"
          />
        </b-form-group>
      </validation-provider>

      <validation-provider
        class="col-md-6"
        vid="estimated_margin"
        :key="`estimated-margin-${model.estimated_amount}-${model.sales_plan_id}`"
        :name="lodash.upperFirst($t('purchase_plans.estimated_margin'))"
        :rules="estimatedMarginRules"
        :custom-messages="{ max_value: $t('purchase_plans.margin_exceeds_amount') }"
        v-slot="{ valid, errors }"
      >
        <b-form-group
          class="row mb-3 asterisk-after-text"
          label-cols-md="12"
          :label="lodash.startCase($t('purchase_plans.estimated_margin'))"
          :state="errors[0] ? false : (valid ? true : null)"
          :invalid-feedback="errors[0]"
        >
          <numeral-strict-input
            v-model.number="model.estimated_margin"
            name="estimated_margin"
            class="text-right"
            format="$ 0,0.[000]"
            :disabled="isEstimatedMarginDisabled"
            :state="errors[0] ? false : (valid ? true : null)"
            :placeholder="lodash.upperFirst($t('purchase_plans.estimated_margin'))"
          />
        </b-form-group>
      </validation-provider>
    </div>

    <b-alert :show="showEstimatedAmountExceedsSalesPlanAlert" variant="warning">
      <p class="processing-alert-message mb-0">
        <i class="mdi mdi-alert-circle-outline"></i>

        {{ $t('purchase_plans.alert_messages.estimated_amount_exceeds_sales_plan_message', { estimated_sale: moneyDisplay(normalizeNumber((selectedSalesPlan || {}).estimated_sale)) }) }}
      </p>
    </b-alert>

    <b-alert :show="showEstimatedAmountExceedsProcessingRemainingAlert" variant="danger">
      <p class="processing-alert-message mb-0">
        <i class="mdi mdi-alert-circle-outline"></i>

        {{ $t('purchase_plans.alert_messages.estimated_amount_exceeds_processing_remaining_message', { remaining: moneyDisplay(normalizeNumber(processingSummary.remaining)) }) }}
      </p>
    </b-alert>
  </validation-observer>
</template>

<script>
import FormModel from './model';
import {daysPastDueVariant} from "@/helpers/ui/badgeVariants";
import ProductSelectAjax from "@/components/inputs/product-select-ajax.vue";
import CustomerSelectAjax from "@/components/inputs/customer-select-ajax.vue";
import VueMultiselectAjax from "@/components/inputs/vue-multiselect-ajax.vue";
import CustomerCreateModal from "@/components/modals/customer-create.vue";
import NumeralStrictInput from "@/components/inputs/numeral-strict";

export default {
  extends: FormModel,
  components: {
    ProductSelectAjax,
    CustomerSelectAjax,
    VueMultiselectAjax,
    CustomerCreateModal,
    NumeralStrictInput,
  },
  created() {
    if (this.model.processing) {
      this.hydrateProcessingSummary(this.model.processing);
    }
    if (this.model.processing_id) {
      this.fetchProcessingSummary(this.model.processing_id);
    }

    if (this.model.product) {
      this.selectedProduct = this.model.product;
    }

    if (this.model.sales_plan) {
      this.selectedSalesPlan = this.model.sales_plan;
      if (this.model.sales_plan.product_type) {
        this.model.product_type = String(this.model.sales_plan.product_type).toLowerCase();
      }
    } else {
      this.model.product_type = 'x';
    }
  },
  data() {
    return {
      model: {
        product_type: 'x',
        payment_type: 'normal',
        _action: 'draft',
        ...this.value,
      },
      selectedProduct: null,
      selectedSalesPlan: null,
      processingSummary: {
        limit: null,
        usage: null,
        remaining: null,
        dpd: null,
      },
      processingSummaryResolved: false,
      processingSummaryError: false,
      processingSummaryRequestId: 0,
      selects: {
        productTypes: [
          { value: 'x', text: 'X (Type I)' },
          { value: 'frs', text: 'FRS (Type II)' },
          { value: 'km', text: 'KM (Type II)' },
          { value: 'jp', text: 'JP (Type II)' },
        ],
      },
    };
  },
  computed: {
    paymentTypes() {
      const options = [
        { value: 'down_payment_80', text: '80%' },
        { value: 'normal', text: this.lodash.upperFirst(this.$t('purchase_plans.payment_types.normal')) },
      ];

      if (this.model.payment_type === 'settlement') {
        options.splice(1, 0, { value: 'settlement', text: this.lodash.upperFirst(this.$t('purchase_plans.payment_types.settlement')) });
      }

      return options;
    },
    isSalesPlanRequired() {
      if (this.selectedProduct) {
        return String(this.selectedProduct.code).toLowerCase() !== 'hybrid';
      }
      return true;
    },
    salesPlanRules() {
      return this.isSalesPlanRequired ? 'required' : '';
    },
    processingRules() {
      return this.isProductX ? '' : 'required';
    },
    estimatedSizeStartRules() {
      const end = this.model.estimated_size_end;
      const hasEnd = end !== null && end !== '' && end !== undefined;
      const max = hasEnd ? end : 100000000000000000000;
      return `required|double|min_value:0|max_value:${max}`;
    },
    estimatedMarginRules() {
      const amount = this.model.estimated_amount;
      const hasAmount = amount !== null && amount !== '' && amount !== undefined;
      const max = hasAmount ? amount : 100000000000000000000;
      return `required|double|min_value:0|max_value:${max}`;
    },
    isProductX() {
      return !this.isSalesPlanRequired;
    },
    salesPlanMarginRatio() {
      const plan = this.selectedSalesPlan;
      if (!plan) {
        return null;
      }
      const sale = Number(plan.estimated_sale);
      const margin = Number(plan.estimated_margin);
      if (!Number.isFinite(sale) || sale === 0 || !Number.isFinite(margin)) {
        return null;
      }
      return margin / sale;
    },
    isEstimatedMarginEditable() {
      if (!this.selectedProduct) {
        return false;
      }
      return String(this.selectedProduct.code).toLowerCase() === 'hybrid';
    },
    isEstimatedMarginDisabled() {
      return !this.isEstimatedMarginEditable || this.isEstimatedMarginAutoCalculated;
    },
    isEstimatedMarginAutoCalculated() {
      return !this.isProductX && !!this.model.sales_plan_id && this.salesPlanMarginRatio !== null;
    },
    autoEstimatedMargin() {
      if (!this.isEstimatedMarginAutoCalculated) {
        return null;
      }
      const amount = Number(this.model.estimated_amount);
      const safeAmount = Number.isFinite(amount) ? amount : 0;
      return Math.round(this.salesPlanMarginRatio * safeAmount);
    },
    isProcessingHasLimit() {
      return this.processingSummary.limit !== null;
    },
    isProcessingHasAvailableLimit() {
      return this.processingSummary.remaining !== null && this.processingSummary.remaining > 0;
    },
    showProcessingDoesntHaveLimitAlert() {
      return this.processingSummaryResolved && !this.processingSummaryError && !!this.model.processing_id && !this.isProcessingHasLimit;
    },
    showProcessingLimitExceededAlert() {
      return this.processingSummaryResolved && !this.processingSummaryError && this.isProcessingHasLimit && !this.isProcessingHasAvailableLimit;
    },
    showProcessingSummaryErrorAlert() {
      return this.processingSummaryResolved && this.processingSummaryError && !!this.model.processing_id;
    },
    isEstimatedAmountExceedsSalesPlan() {
      if (!this.model.sales_plan_id || !this.selectedSalesPlan) {
        return false;
      }
      const amount = this.normalizeNumber(this.model.estimated_amount);
      const salesPlanEstimate = this.normalizeNumber(this.selectedSalesPlan.estimated_sale);
      if (amount === null || salesPlanEstimate === null) {
        return false;
      }
      return amount > salesPlanEstimate;
    },
    showEstimatedAmountExceedsSalesPlanAlert() {
      return this.isEstimatedAmountExceedsSalesPlan;
    },
    isEstimatedAmountExceedsProcessingRemaining() {
      if (!this.isProductX || !this.model.processing_id) {
        return false;
      }
      if (!this.processingSummaryResolved || this.processingSummaryError || !this.isProcessingHasLimit) {
        return false;
      }
      const amount = this.normalizeNumber(this.model.estimated_amount);
      const remaining = this.normalizeNumber(this.processingSummary.remaining);
      if (amount === null || remaining === null) {
        return false;
      }
      return amount > remaining;
    },
    showEstimatedAmountExceedsProcessingRemainingAlert() {
      return this.isEstimatedAmountExceedsProcessingRemaining;
    },
    isProcessingSummaryBlockingSubmission() {
      return this.isEstimatedAmountExceedsProcessingRemaining;
    },
  },
  watch: {
    'model.product_id'(newValue) {
      if (!newValue) {
        this.selectedProduct = null;
      }
    },
    'model.product_type'(value) {
      if ((value || '').toLowerCase() === 'x') {
        this.model.sales_plan_id = null;
        this.selectedSalesPlan = null;
      }
    },
    isProcessingSummaryBlockingSubmission: {
      immediate: true,
      handler(value) {
        this.$emit('limit-validation-change', value);
      },
    },
    isEstimatedMarginDisabled: {
      immediate: true,
      handler() {
        this.syncEstimatedMargin();
      },
    },
    'model.sales_plan_id'() {
      if (!this.isEstimatedMarginEditable) {
        this.model.estimated_margin = null;
      }
      this.syncEstimatedMargin();
    },
    autoEstimatedMargin: {
      immediate: true,
      handler(value) {
        if (this.isEstimatedMarginAutoCalculated && value !== null && this.model.estimated_margin !== value) {
          this.model.estimated_margin = value;
        }
      },
    },
  },
  methods: {
    daysPastDueVariant,
    formattedDaysPastDue(days_past_due) {
      return days_past_due ?? '-';
    },
    salesPlanLabel(item) {
      if (!item) return '';
      return `${item.name || item.id}`;
    },
    syncEstimatedMargin() {
      if (this.isEstimatedMarginAutoCalculated) {
        if (this.autoEstimatedMargin !== null && this.model.estimated_margin !== this.autoEstimatedMargin) {
          this.model.estimated_margin = this.autoEstimatedMargin;
        }
        return;
      }
      const margin = this.model.estimated_margin;
      const isEmpty = margin === null || margin === '' || margin === undefined;
      if (this.isEstimatedMarginDisabled && isEmpty) {
        this.model.estimated_margin = 0;
      }
    },
    onProductSelected(value) {
      this.selectedProduct = value || null;

      const currentPlanProductId = (this.selectedSalesPlan || {}).product_id;
      if (this.model.sales_plan_id && currentPlanProductId
        && (!value || Number(currentPlanProductId) !== Number(value.id))) {
        this.model.sales_plan_id = null;
        this.selectedSalesPlan = null;
      }

      if (!value) {
        if (!this.model.sales_plan_id) {
          this.model.product_type = 'x';
        }
      } else {
        if (!this.model.sales_plan_id) {
          this.model.product_type = String(value.code || 'x').toLowerCase();
        }
      }
    },
    onSalesPlanSelected(value) {
      this.selectedSalesPlan = value || null;
      if ((value || {}).product_id && !this.model.product_id) {
        this.model.product_id = value.product_id;
      }
      if ((value || {}).product_type) {
        this.model.product_type = String(value.product_type).toLowerCase();
      } else if ((value || {}).product) {
        this.model.product_type = String(value.product.code || 'x').toLowerCase();
      } else if (!value) {
        this.model.product_type = String((this.selectedProduct || {}).code || 'x').toLowerCase();
      }
      this.syncEstimatedMargin();
    },
    onProcessingSelected(value) {
      if (!value || !value.id) {
        this.processingSummaryRequestId += 1;
        this.processingSummary = { limit: null, usage: null, remaining: null, dpd: null };
        this.processingSummaryResolved = false;
        this.processingSummaryError = false;
        return;
      }

      this.processingSummaryError = false;
      this.fetchProcessingSummary(value.id);
    },
    onProcessingCreated(value) {
      this.$set(this.model, 'processing_id', value.id);
      this.processingSummaryError = false;
      this.fetchProcessingSummary(value.id);
      this.$nextTick(() => {
        if (this.$refs.processingSelect && this.$refs.processingSelect.$refs && this.$refs.processingSelect.$refs.multiselect) {
          this.$refs.processingSelect.$refs.multiselect.fetchOptions();
        }
      });
    },
    retryProcessingSummary() {
      if (!this.model.processing_id) {
        return;
      }

      this.processingSummaryResolved = false;
      this.processingSummaryError = false;
      this.fetchProcessingSummary(this.model.processing_id);
    },
    fetchProcessingSummary(id) {
      const requestId = ++this.processingSummaryRequestId;
      this.processingSummaryResolved = false;
      return this.$axios.get(`/v1/processing-customers/${id}`).then(({ data }) => {
        if (requestId !== this.processingSummaryRequestId) {
          return null;
        }

        const payload = (data || {}).data || {};
        this.hydrateProcessingSummary(payload);
      }).catch(() => {
        if (requestId !== this.processingSummaryRequestId) {
          return null;
        }

        this.processingSummaryError = true;
        this.processingSummary = { limit: null, usage: null, remaining: null, dpd: null };
      }).finally(() => {
        if (requestId === this.processingSummaryRequestId) {
          this.processingSummaryResolved = true;
        }
      });
    },
    processingCustomerLabel(value) {
      if (!value) return '';
      const { code, name } = value;
      return `${code} - ${name}`;
    },
    hydrateProcessingSummary(customer) {
      const summary = (customer || {}).limit_summary || {};
      const group = (((customer || {}).odoo_res_partner || {}).odoo_obligor_limit || {}).odoo_obligor_limit_group || null;

      const configuredLimitAmount = group !== null ? this.normalizeNumber(group.limit_amount) : null;
      const hasConfiguredLimit = configuredLimitAmount !== null && configuredLimitAmount > 0;

      const limit = hasConfiguredLimit
        ? this.normalizeNumber(summary.limit_amount ?? group.limit_amount)
        : null;

      const usage = hasConfiguredLimit
        ? this.normalizeNumber(summary.used_amount_adjusted ?? group.used_amount)
        : null;
      const remaining = hasConfiguredLimit
        ? this.normalizeNumber(summary.remaining_amount_adjusted ?? group.available_amount)
        : null;
      const dpd = this.normalizeNumber(
        (customer || {}).odoo_res_partner?.max_days_past_due_in_90_days
        ?? (customer || {}).max_days_past_due_in_90_days
      );

      this.processingSummary = {
        limit,
        usage,
        remaining: remaining === null && limit !== null && usage !== null ? (limit - usage) : remaining,
        dpd,
      };
    },
    normalizeNumber(value) {
      if (value === null || value === undefined || value === '') return null;
      const number = Number(value);
      return Number.isFinite(number) ? number : null;
    },
    moneyDisplay(value) {
      if (value === null || value === undefined) return '-';
      return this.$numeral(value).format('$ 0,0.[000]');
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../design/custom/components/status-badge";
::v-deep .asterisk-after-text .col-form-label::after {
  content: " *";
  color: #DC2626;
}

::v-deep .col-form-label {
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #182230;
}



.alert.alert-danger {
  border-color: #F87171;
  .alert-heading {
    color: #DC2626;
    font-size: 14px;
  }
  .processing-alert-message {
    color: #DC2626;
    font-size: 14px;
  }
}

.alert.alert-warning {
  border-color: #FEC84B;
  background-color: #FEF0C7;
  .processing-alert-message {
    color: #7A2E0E;
    font-size: 14px;
    i {
      color: #F79009;
      font-size: 20px;
    }    
  }
}

.text-right {
  text-align: right;
}
</style>
